import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  AcademicCapIcon,
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  PencilSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import {
  getFeedbackStats,
  getPendingReviews,
  reviewExample,
  getAllFeedback,
  getCategoryAnalytics,
  getWeeklyTrends,
  getAllExamples,
  updateExample,
  createManualExample,
  bulkReviewExamples,
  exportExamplesJsonl,
  regenerateSnapshot,
  getOriginalResponse,
  suggestImprovement,
  type FewShotExample,
  type FeedbackStats,
  type FeedbackItem,
  type CategoryAnalytics,
  type WeeklyTrend,
} from './fewShotReviewApi';

type TabId = 'overview' | 'explorer' | 'examples' | 'analytics';

const TABS: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'explorer', label: 'Feedback Explorer' },
  { id: 'examples', label: 'Training Examples' },
  { id: 'analytics', label: 'Analytics' },
];

// ═══════════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════

const FewShotReviewDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const loadStats = useCallback(async () => {
    try {
      const data = await getFeedbackStats();
      setStats(data);
    } catch {
      /* stats are best-effort */
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
  }, []);

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/system-monitor')}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <AcademicCapIcon className="w-7 h-7 text-lex-accent" />
              Lex Training Center
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Manage feedback, training examples, and track Lex quality
            </p>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium ${
          toast.type === 'success'
            ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300 border border-green-200 dark:border-green-800'
            : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          {toast.message}
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <StatCard label="Total Feedback" value={stats.total} color="gray" />
            <StatCard label="Thumbs Up" value={stats.thumbsUp} color="green" icon={<HandThumbUpIcon className="w-5 h-5" />} />
            <StatCard label="Thumbs Down" value={stats.thumbsDown} color="red" icon={<HandThumbDownIcon className="w-5 h-5" />} />
            <StatCard label="With Corrections" value={stats.withCorrections} color="amber" />
          </div>
          {/* Classification breakdown — helps trainers understand signal quality */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <StatCard label="Trainable (Refinement)" value={stats.refinementCount} color="emerald" />
            <StatCard label="Tool Failures (Skip)" value={stats.toolFailureCount} color="orange" />
            <StatCard label="Unclassified" value={stats.unknownClassCount} color="gray" />
          </div>
          {/* Implicit signal breakdown — auto-detected from conversation patterns */}
          {(stats.totalImplicit ?? 0) > 0 && (
            <div className="grid grid-cols-4 gap-4 mb-6">
              <StatCard label="Rephrases Detected" value={stats.implicitRephrase ?? 0} color="purple" />
              <StatCard label="Topic Abandons" value={stats.implicitAbandon ?? 0} color="red" />
              <StatCard label="Inline Corrections" value={stats.implicitCorrection ?? 0} color="amber" />
              <StatCard label="Natural Follow-ups" value={stats.implicitFollowup ?? 0} color="emerald" />
            </div>
          )}
        </>
      )}

      {/* Tab Bar */}
      <div className="flex gap-1 mb-6 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === tab.id
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab showToast={showToast} onRefresh={loadStats} />}
      {activeTab === 'explorer' && <FeedbackExplorerTab showToast={showToast} />}
      {activeTab === 'examples' && <ExamplesTab showToast={showToast} />}
      {activeTab === 'analytics' && <AnalyticsTab />}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// STAT CARD
// ═══════════════════════════════════════════════════════════════════

const colorMap: Record<string, string> = {
  gray: 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white',
  green: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300',
  red: 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300',
  amber: 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300',
  emerald: 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300',
  orange: 'bg-orange-50 dark:bg-orange-900/10 border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300',
};

const StatCard: React.FC<{ label: string; value: number; color: string; icon?: React.ReactNode }> = ({ label, value, color, icon }) => (
  <div className={`p-4 rounded-xl border ${colorMap[color] || colorMap.gray}`}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-sm font-medium opacity-70">{label}</span>
      {icon}
    </div>
    <span className="text-3xl font-bold">{value}</span>
  </div>
);

// ═══════════════════════════════════════════════════════════════════
// TAB 1: OVERVIEW (Pending Reviews)
// ═══════════════════════════════════════════════════════════════════

const OverviewTab: React.FC<{ showToast: (m: string, t: 'success' | 'error') => void; onRefresh: () => void }> = ({ showToast, onRefresh }) => {
  const [pending, setPending] = useState<FewShotExample[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const loadPending = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPendingReviews();
      setPending(data.examples);
    } catch {
      showToast('Failed to load pending reviews', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadPending(); }, [loadPending]);

  const handleReview = useCallback(async (exampleId: string, action: 'approve' | 'reject') => {
    setActionInProgress(exampleId);
    try {
      await reviewExample(exampleId, action);
      setPending((prev) => prev.filter((ex) => ex.id !== exampleId));
      showToast(action === 'approve' ? 'Example approved' : 'Example rejected', 'success');
      onRefresh();
    } catch {
      showToast('Failed to submit review', 'error');
    } finally {
      setActionInProgress(null);
    }
  }, [showToast, onRefresh]);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Pending Reviews</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {pending.length === 0 ? 'No examples awaiting review' : `${pending.length} example${pending.length > 1 ? 's' : ''} flagged for human review`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            pending.length > 0
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300'
              : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
          }`}>
            {pending.length} pending
          </span>
          <button onClick={loadPending} disabled={loading} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
            <ArrowPathIcon className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-gray-400">
          <ArrowPathIcon className="w-8 h-8 mx-auto animate-spin mb-2" />Loading...
        </div>
      ) : pending.length === 0 ? (
        <div className="p-12 text-center">
          <CheckCircleIcon className="w-12 h-12 mx-auto text-green-400 mb-3" />
          <p className="text-gray-500 dark:text-gray-400">All clear — no examples need review.</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {pending.map((example) => (
            <ReviewCard
              key={example.id}
              example={example}
              onApprove={() => handleReview(example.id, 'approve')}
              onReject={() => handleReview(example.id, 'reject')}
              disabled={actionInProgress === example.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// TAB 2: FEEDBACK EXPLORER
// ═══════════════════════════════════════════════════════════════════

const FeedbackExplorerTab: React.FC<{ showToast: (m: string, t: 'success' | 'error') => void }> = ({ showToast }) => {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [ratingFilter, setRatingFilter] = useState<'' | 'up' | 'down'>('');
  const [curationFilter, setCurationFilter] = useState<'' | 'promoted' | 'rejected' | 'pending'>('');
  const [classFilter, setClassFilter] = useState<'' | 'refinement' | 'tool_failure' | 'unknown'>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const limit = 15;

  const loadFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllFeedback({
        page,
        limit,
        rating: ratingFilter || undefined,
        search: search || undefined,
        curationResult: curationFilter || undefined,
        feedbackClass: classFilter || undefined,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch {
      showToast('Failed to load feedback', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, ratingFilter, curationFilter, classFilter, search, showToast]);

  useEffect(() => { loadFeedback(); }, [loadFeedback]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4">
      {/* Filters Bar */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search questions, corrections, responses..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-lex-accent focus:border-transparent"
          />
        </div>
        <div className="flex items-center gap-1">
          <FunnelIcon className="w-4 h-4 text-gray-400" />
          <select
            value={ratingFilter}
            onChange={(e) => { setRatingFilter(e.target.value as '' | 'up' | 'down'); setPage(1); }}
            className="text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg px-3 py-2"
          >
            <option value="">All ratings</option>
            <option value="up">Thumbs Up</option>
            <option value="down">Thumbs Down</option>
          </select>
          <select
            value={curationFilter}
            onChange={(e) => { setCurationFilter(e.target.value as '' | 'promoted' | 'rejected' | 'pending'); setPage(1); }}
            className="text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg px-3 py-2"
          >
            <option value="">All curation</option>
            <option value="promoted">Promoted</option>
            <option value="rejected">Rejected</option>
            <option value="pending">Pending</option>
          </select>
          <select
            value={classFilter}
            onChange={(e) => { setClassFilter(e.target.value as '' | 'refinement' | 'tool_failure' | 'unknown'); setPage(1); }}
            className="text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg px-3 py-2"
          >
            <option value="">All types</option>
            <option value="refinement">Trainable</option>
            <option value="tool_failure">Tool Failures</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>
        <span className="text-sm text-gray-500">{total} results</span>
      </div>

      {/* Feedback List */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">
            <ArrowPathIcon className="w-8 h-8 mx-auto animate-spin mb-2" />Loading...
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No feedback found.</div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {items.map((item) => (
              <div key={item.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {item.rating === 'up' ? (
                        <HandThumbUpIcon className="w-4 h-4 text-green-500" />
                      ) : (
                        <HandThumbDownIcon className="w-4 h-4 text-red-500" />
                      )}
                      {item.intent_category && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                          {item.intent_category}
                        </span>
                      )}
                      {item.curation_result && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.curation_result === 'promoted'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                        }`}>
                          {item.curation_result}
                        </span>
                      )}
                      {/* Feedback classification badge */}
                      {item.feedback_class && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.feedback_class === 'refinement'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
                            : item.feedback_class === 'tool_failure'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                        }`} title={
                          item.feedback_class === 'refinement'
                            ? 'Trainable: response wording can be improved via few-shot'
                            : item.feedback_class === 'tool_failure'
                            ? 'Tool failure: wrong/missing data — fix the tool pipeline, not the LLM'
                            : 'Classification unknown'
                        }>
                          {item.feedback_class === 'refinement' ? 'Trainable' : item.feedback_class === 'tool_failure' ? 'Tool Failure' : 'Unknown'}
                        </span>
                      )}
                      {/* Tool ID badge */}
                      {item.tool_id && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-mono bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-300">
                          {item.tool_id}
                        </span>
                      )}
                      <span className="text-xs text-gray-400">
                        {new Date(item.created_at).toLocaleDateString()} {new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Q: &ldquo;{item.user_question}&rdquo;
                    </p>
                  </div>
                  <button
                    onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
                  >
                    <EyeIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Expanded: Side-by-Side Diff + Curation Info */}
                {expandedId === item.id && (
                  <div className="mt-3 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="bg-red-50 dark:bg-red-900/10 rounded-lg p-3 border border-red-200 dark:border-red-800">
                        <span className="text-xs font-semibold text-red-600 dark:text-red-400 block mb-1">Lex Response</span>
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {item.refined_response || item.agent_response || '(no response recorded)'}
                        </p>
                      </div>
                      {item.correction ? (
                        <div className="bg-green-50 dark:bg-green-900/10 rounded-lg p-3 border border-green-200 dark:border-green-800">
                          <span className="text-xs font-semibold text-green-600 dark:text-green-400 block mb-1">User Correction</span>
                          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{item.correction}</p>
                        </div>
                      ) : (
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                          <span className="text-xs font-semibold text-gray-500 block mb-1">No Correction</span>
                          <p className="text-sm text-gray-400">
                            {item.rating === 'up' ? 'User approved this response.' : 'User downvoted without providing a correction.'}
                          </p>
                        </div>
                      )}
                    </div>
                    {/* Curation Decision */}
                    {item.curation_result && (
                      <div className={`rounded-lg p-3 border ${
                        item.curation_result === 'promoted'
                          ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800'
                          : 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold ${
                            item.curation_result === 'promoted'
                              ? 'text-blue-600 dark:text-blue-400'
                              : 'text-amber-600 dark:text-amber-400'
                          }`}>
                            LLM Curation: {item.curation_result.toUpperCase()}
                          </span>
                          {item.curated_at && (
                            <span className="text-xs text-gray-400">
                              {new Date(item.curated_at).toLocaleDateString()} {new Date(item.curated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                        {item.curation_reason && (() => {
                          const modelMatch = item.curation_reason.match(/^\[([^\]]+)\]\s*/);
                          const modelName = modelMatch ? modelMatch[1] : null;
                          const reasonText = modelMatch ? item.curation_reason.slice(modelMatch[0].length) : item.curation_reason;
                          return (
                            <div className="flex items-start gap-2">
                              {modelName && (
                                <span className="inline-flex items-center shrink-0 px-2 py-0.5 rounded text-xs font-mono font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border border-purple-200 dark:border-purple-700">
                                  {modelName}
                                </span>
                              )}
                              <p className="text-sm text-gray-700 dark:text-gray-300">{reasonText}</p>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Page {page} of {totalPages} ({total} total)
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// TAB 3: TRAINING EXAMPLES (Editor + Create + Bulk + Export)
// ═══════════════════════════════════════════════════════════════════

const CATEGORY_OPTIONS = ['summary', 'case_number', 'billing', 'tasks', 'events', 'parties', 'attorney', 'court', 'document', 'correction', 'general'];

const ExamplesTab: React.FC<{ showToast: (m: string, t: 'success' | 'error') => void }> = ({ showToast }) => {
  const [examples, setExamples] = useState<FewShotExample[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ question: '', ideal_response: '', category: '', data_snapshot: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ question: '', idealResponse: '', category: 'general', dataSnapshot: '{}' });
  const [filterActive, setFilterActive] = useState<'' | 'true' | 'false'>('');
  const [filterCategory, setFilterCategory] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [originalResponse, setOriginalResponse] = useState<string | null>(null);
  const [loadingOriginal, setLoadingOriginal] = useState(false);

  const handleRegenerateSnapshot = async (mode: 'edit' | 'create') => {
    const question = mode === 'edit' ? editForm.question : createForm.question;
    const idealResponse = mode === 'edit' ? editForm.ideal_response : createForm.idealResponse;
    const category = mode === 'edit' ? editForm.category : createForm.category;

    if (!question || !idealResponse) {
      showToast('Question and Ideal Response are required to regenerate', 'error');
      return;
    }

    setRegenerating(true);
    try {
      const result = await regenerateSnapshot({ question, idealResponse, category });
      if (mode === 'edit') {
        setEditForm({ ...editForm, data_snapshot: result.dataSnapshot });
      } else {
        setCreateForm({ ...createForm, dataSnapshot: result.dataSnapshot });
      }
      showToast('Snapshot regenerated', 'success');
    } catch {
      showToast('Failed to regenerate snapshot', 'error');
    } finally {
      setRegenerating(false);
    }
  };

  const loadExamples = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllExamples({
        isActive: filterActive ? filterActive === 'true' : undefined,
        category: filterCategory || undefined,
      });
      setExamples(data.examples);
    } catch {
      showToast('Failed to load examples', 'error');
    } finally {
      setLoading(false);
    }
  }, [filterActive, filterCategory, showToast]);

  useEffect(() => { loadExamples(); }, [loadExamples]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === examples.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(examples.map((e) => e.id)));
    }
  };

  const handleBulkAction = async (action: 'approve' | 'reject') => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      await bulkReviewExamples(Array.from(selectedIds), action);
      showToast(`${action === 'approve' ? 'Approved' : 'Rejected'} ${selectedIds.size} examples`, 'success');
      setSelectedIds(new Set());
      loadExamples();
    } catch {
      showToast('Bulk action failed', 'error');
    } finally {
      setBulkLoading(false);
    }
  };

  const startEdit = (ex: FewShotExample) => {
    setEditingId(ex.id);
    setEditForm({
      question: ex.question,
      ideal_response: ex.ideal_response,
      category: ex.category,
      data_snapshot: ex.data_snapshot,
    });
    // Fetch original response for side-by-side preview
    setOriginalResponse(null);
    if (ex.source_feedback_id) {
      setLoadingOriginal(true);
      getOriginalResponse(ex.id)
        .then((r) => setOriginalResponse(r.originalResponse))
        .catch(() => setOriginalResponse(null))
        .finally(() => setLoadingOriginal(false));
    }
  };

  const handleSuggestImprovement = async () => {
    const source = originalResponse || editForm.ideal_response;
    if (!editForm.question || !source) {
      showToast('Question and a response are required to suggest improvements', 'error');
      return;
    }

    setSuggesting(true);
    try {
      const result = await suggestImprovement({
        question: editForm.question,
        originalResponse: source,
        currentIdealResponse: editForm.ideal_response !== source ? editForm.ideal_response : undefined,
        category: editForm.category,
      });
      setEditForm((prev) => ({ ...prev, ideal_response: result.suggestedResponse }));
      showToast('AI suggestion applied to Ideal Response', 'success');
    } catch {
      showToast('Failed to generate suggestion', 'error');
    } finally {
      setSuggesting(false);
    }
  };

  const saveEdit = async () => {
    if (!editingId) return;
    try {
      await updateExample(editingId, editForm);
      showToast('Example updated', 'success');
      setEditingId(null);
      loadExamples();
    } catch {
      showToast('Failed to update example', 'error');
    }
  };

  const handleCreate = async () => {
    try {
      await createManualExample(createForm);
      showToast('Example created', 'success');
      setShowCreate(false);
      setCreateForm({ question: '', idealResponse: '', category: 'general', dataSnapshot: '{}' });
      loadExamples();
    } catch {
      showToast('Failed to create example', 'error');
    }
  };

  const handleExport = async () => {
    try {
      const blob = await exportExamplesJsonl();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lex-training-examples-${new Date().toISOString().slice(0, 10)}.jsonl`;
      a.click();
      window.URL.revokeObjectURL(url);
      showToast('Export downloaded', 'success');
    } catch {
      showToast('Export failed', 'error');
    }
  };

  const uniqueCategories = [...new Set(examples.map((e) => e.category))].sort();

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-2 items-center">
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value as '' | 'true' | 'false')}
            className="text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg px-3 py-2"
          >
            <option value="">All status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg px-3 py-2"
          >
            <option value="">All categories</option>
            {uniqueCategories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <span className="text-sm text-gray-500">{examples.length} examples</span>
        </div>
        <div className="flex gap-2">
          {selectedIds.size > 0 && (
            <>
              <button
                onClick={() => handleBulkAction('approve')}
                disabled={bulkLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-300 dark:hover:bg-green-900/40 disabled:opacity-40"
              >
                <CheckCircleIcon className="w-4 h-4" />Approve ({selectedIds.size})
              </button>
              <button
                onClick={() => handleBulkAction('reject')}
                disabled={bulkLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-300 dark:hover:bg-red-900/40 disabled:opacity-40"
              >
                <XCircleIcon className="w-4 h-4" />Reject ({selectedIds.size})
              </button>
            </>
          )}
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-lex-accent text-white hover:opacity-90 transition-opacity"
          >
            <PlusIcon className="w-4 h-4" />Create Example
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />Export JSONL
          </button>
        </div>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border-2 border-lex-accent p-5 space-y-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Create Manual Example</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Category</label>
              <select
                value={createForm.category}
                onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
                className="w-full text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg px-3 py-2"
              >
                {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Question</label>
              <input
                value={createForm.question}
                onChange={(e) => setCreateForm({ ...createForm, question: e.target.value })}
                placeholder="What would the user ask?"
                className="w-full text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg px-3 py-2"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 block mb-1">Ideal Response</label>
            <textarea
              value={createForm.idealResponse}
              onChange={(e) => setCreateForm({ ...createForm, idealResponse: e.target.value })}
              rows={3}
              placeholder="The perfect response Lex should give..."
              className="w-full text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-gray-500">Data Snapshot (JSON)</label>
              <button
                onClick={() => handleRegenerateSnapshot('create')}
                disabled={regenerating}
                className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 disabled:opacity-50 transition-colors"
              >
                <ArrowPathIcon className={`w-3 h-3 ${regenerating ? 'animate-spin' : ''}`} />
                {regenerating ? 'Regenerating…' : 'Regenerate Snapshot'}
              </button>
            </div>
            <textarea
              value={createForm.dataSnapshot}
              onChange={(e) => setCreateForm({ ...createForm, dataSnapshot: e.target.value })}
              rows={2}
              className="w-full text-sm font-mono border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg px-3 py-2"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowCreate(false)} className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Cancel</button>
            <button
              onClick={handleCreate}
              disabled={!createForm.question || !createForm.idealResponse}
              className="px-4 py-1.5 text-sm font-medium rounded-lg bg-lex-accent text-white hover:opacity-90 disabled:opacity-40"
            >
              Create
            </button>
          </div>
        </div>
      )}

      {/* Examples Table */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400"><ArrowPathIcon className="w-8 h-8 mx-auto animate-spin mb-2" />Loading...</div>
        ) : examples.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No training examples yet.</div>
        ) : (
          <>
            {/* Header row */}
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3 text-xs font-medium text-gray-500 uppercase">
              <input type="checkbox" checked={selectedIds.size === examples.length && examples.length > 0} onChange={toggleSelectAll} className="rounded" />
              <span className="flex-1">Question</span>
              <span className="w-24 text-center">Category</span>
              <span className="w-16 text-center">Quality</span>
              <span className="w-16 text-center">Status</span>
              <span className="w-20 text-center">Actions</span>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {examples.map((ex) => (
                <div key={ex.id}>
                  <div className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(ex.id)}
                      onChange={() => toggleSelect(ex.id)}
                      className="rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 dark:text-white truncate">{ex.question}</p>
                      <p className="text-xs text-gray-400 truncate">{ex.ideal_response.slice(0, 80)}...</p>
                    </div>
                    <span className="w-24 text-center">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                        {ex.category}
                      </span>
                      {ex.signal_type && ex.signal_type.startsWith('implicit_') && (
                        <span className="block mt-0.5 px-1.5 py-0 rounded text-[10px] font-medium bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">
                          {ex.signal_type.replace('implicit_', '')}
                        </span>
                      )}
                    </span>
                    <span className="w-16 text-center text-sm font-medium">
                      {(Number(ex.quality_score) * 100).toFixed(0)}%
                    </span>
                    <span className="w-16 text-center">
                      <span className={`inline-block w-2 h-2 rounded-full ${ex.is_active ? 'bg-green-400' : 'bg-gray-300'}`} />
                      {ex.needs_review && <span className="ml-1 text-xs text-amber-500">review</span>}
                    </span>
                    <span className="w-20 text-center">
                      <button onClick={() => startEdit(ex)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                        <PencilSquareIcon className="w-4 h-4 text-gray-400" />
                      </button>
                    </span>
                  </div>

                  {/* Inline Editor with Side-by-Side Preview */}
                  {editingId === ex.id && (
                    <div className="px-4 py-3 bg-blue-50 dark:bg-blue-900/10 border-t border-blue-200 dark:border-blue-800 space-y-3">
                      {/* Row 1: Question + Category */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-gray-500 block mb-1">Question</label>
                          <input
                            value={editForm.question}
                            onChange={(e) => setEditForm({ ...editForm, question: e.target.value })}
                            className="w-full text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 block mb-1">Category</label>
                          <select
                            value={editForm.category}
                            onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                            className="w-full text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg px-3 py-2"
                          >
                            {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                      </div>

                      {/* Row 2: Side-by-Side — Original Response (read-only) | Ideal Response (editable) */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* Left: What Lex Said (read-only) */}
                        <div>
                          <label className="text-xs font-medium text-red-400 block mb-1">
                            What Lex Said (Original)
                          </label>
                          {loadingOriginal ? (
                            <div className="w-full h-[120px] flex items-center justify-center border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900">
                              <ArrowPathIcon className="w-4 h-4 animate-spin text-gray-400" />
                            </div>
                          ) : originalResponse ? (
                            <textarea
                              value={originalResponse}
                              readOnly
                              rows={6}
                              className="w-full text-sm border border-red-200 dark:border-red-800/40 bg-red-50/50 dark:bg-red-900/10 text-gray-700 dark:text-gray-300 rounded-lg px-3 py-2 cursor-default opacity-80"
                            />
                          ) : (
                            <div className="w-full h-[120px] flex items-center justify-center border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 text-xs text-gray-400">
                              {ex.source_feedback_id ? 'Original response not found' : 'Manual example — no original response'}
                            </div>
                          )}
                        </div>

                        {/* Right: Ideal Response (editable) */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-xs font-medium text-green-500">
                              What Lex Should Say (Ideal)
                            </label>
                            <button
                              onClick={handleSuggestImprovement}
                              disabled={suggesting}
                              className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-purple-500/10 text-purple-600 dark:text-purple-400 hover:bg-purple-500/20 disabled:opacity-50 transition-colors"
                            >
                              <SparklesIcon className={`w-3 h-3 ${suggesting ? 'animate-pulse' : ''}`} />
                              {suggesting ? 'Thinking…' : 'Suggest Improvement'}
                            </button>
                          </div>
                          <textarea
                            value={editForm.ideal_response}
                            onChange={(e) => setEditForm({ ...editForm, ideal_response: e.target.value })}
                            rows={6}
                            className="w-full text-sm border border-green-200 dark:border-green-800/40 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg px-3 py-2"
                          />
                        </div>
                      </div>

                      {/* Row 3: Data Snapshot */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs font-medium text-gray-500">Data Snapshot (JSON)</label>
                          <button
                            onClick={() => handleRegenerateSnapshot('edit')}
                            disabled={regenerating}
                            className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 disabled:opacity-50 transition-colors"
                          >
                            <ArrowPathIcon className={`w-3 h-3 ${regenerating ? 'animate-spin' : ''}`} />
                            {regenerating ? 'Regenerating…' : 'Regenerate Snapshot'}
                          </button>
                        </div>
                        <textarea
                          value={editForm.data_snapshot}
                          onChange={(e) => setEditForm({ ...editForm, data_snapshot: e.target.value })}
                          rows={2}
                          className="w-full text-sm font-mono border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg px-3 py-2"
                        />
                      </div>

                      {/* Row 4: Actions */}
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => { setEditingId(null); setOriginalResponse(null); }} className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Cancel</button>
                        <button onClick={saveEdit} className="px-4 py-1.5 text-sm font-medium rounded-lg bg-lex-accent text-white hover:opacity-90">Save</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// TAB 4: ANALYTICS
// ═══════════════════════════════════════════════════════════════════

const AnalyticsTab: React.FC = () => {
  const [categories, setCategories] = useState<CategoryAnalytics[]>([]);
  const [trends, setTrends] = useState<WeeklyTrend[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getCategoryAnalytics(),
      getWeeklyTrends(12),
    ]).then(([catData, trendData]) => {
      setCategories(catData.categories);
      setTrends(trendData.trends);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="p-12 text-center text-gray-400"><ArrowPathIcon className="w-8 h-8 mx-auto animate-spin mb-2" />Loading analytics...</div>;
  }

  const maxCatTotal = Math.max(...categories.map((c) => c.total), 1);
  const maxTrendTotal = Math.max(...trends.map((t) => t.total), 1);

  return (
    <div className="space-y-6">
      {/* Category Breakdown */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Category Breakdown</h3>
        {categories.length === 0 ? (
          <p className="text-gray-500 text-sm">No feedback data yet.</p>
        ) : (
          <div className="space-y-3">
            {categories.map((cat) => {
              const downRate = cat.total > 0 ? (cat.thumbsDown / cat.total) * 100 : 0;
              return (
                <div key={cat.category}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{cat.category}</span>
                      {cat.promotedCount > 0 && (
                        <span className="text-xs text-green-600 dark:text-green-400">+{cat.promotedCount} promoted</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="text-green-600">{cat.thumbsUp} up</span>
                      <span className="text-red-600">{cat.thumbsDown} down</span>
                      <span>{cat.withCorrections} corrections</span>
                      <span className="font-medium">{cat.total} total</span>
                    </div>
                  </div>
                  <div className="flex h-5 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800">
                    <div className="bg-green-400 transition-all" style={{ width: `${(cat.thumbsUp / maxCatTotal) * 100}%` }} />
                    <div className="bg-red-400 transition-all" style={{ width: `${(cat.thumbsDown / maxCatTotal) * 100}%` }} />
                  </div>
                  {downRate > 50 && (
                    <p className="text-xs text-red-500 mt-1">High negative rate ({downRate.toFixed(0)}%) — Lex may be weak in this category</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Weekly Trends */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Weekly Quality Trend</h3>
        {trends.length === 0 ? (
          <p className="text-gray-500 text-sm">Not enough data for trends yet.</p>
        ) : (
          <div className="space-y-2">
            <div className="flex items-end gap-1 h-32">
              {trends.map((t) => {
                const height = maxTrendTotal > 0 ? (t.total / maxTrendTotal) * 100 : 0;
                const greenPct = t.total > 0 ? (t.thumbsUp / t.total) * 100 : 0;
                return (
                  <div key={t.week} className="flex-1 flex flex-col items-center group relative">
                    <div className="w-full rounded-t-sm overflow-hidden flex flex-col justify-end" style={{ height: `${Math.max(height, 4)}%` }}>
                      <div className="bg-green-400 w-full" style={{ height: `${greenPct}%` }} />
                      <div className="bg-red-400 w-full" style={{ height: `${100 - greenPct}%` }} />
                    </div>
                    <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                      {new Date(t.week).toLocaleDateString()}: {t.thumbsUp} up / {t.thumbsDown} down ({(t.approvalRate * 100).toFixed(0)}%)
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between text-xs text-gray-400">
              <span>{trends.length > 0 ? new Date(trends[0].week).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}</span>
              <span>{trends.length > 0 ? new Date(trends[trends.length - 1].week).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}</span>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-400 inline-block" /> Thumbs Up</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-400 inline-block" /> Thumbs Down</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// REVIEW CARD (used in Overview tab)
// ═══════════════════════════════════════════════════════════════════

const ReviewCard: React.FC<{
  example: FewShotExample;
  onApprove: () => void;
  onReject: () => void;
  disabled: boolean;
}> = ({ example, onApprove, onReject, disabled }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="p-5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
              {example.category}
            </span>
            <span className="text-xs text-gray-400">Quality: {(Number(example.quality_score) * 100).toFixed(0)}%</span>
            <span className="text-xs text-gray-400">{new Date(example.created_at).toLocaleDateString()}</span>
          </div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">Q: &ldquo;{example.question}&rdquo;</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={onApprove} disabled={disabled}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-300 dark:hover:bg-green-900/40 disabled:opacity-40">
            <CheckCircleIcon className="w-4 h-4" />Approve
          </button>
          <button onClick={onReject} disabled={disabled}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-300 dark:hover:bg-red-900/40 disabled:opacity-40">
            <XCircleIcon className="w-4 h-4" />Reject
          </button>
        </div>
      </div>
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-sm text-gray-700 dark:text-gray-300">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Ideal response:</span>
        <p className="whitespace-pre-wrap">
          {expanded ? example.ideal_response : example.ideal_response.slice(0, 200)}
          {!expanded && example.ideal_response.length > 200 && (
            <button onClick={() => setExpanded(true)} className="text-lex-accent hover:underline ml-1">...show more</button>
          )}
          {expanded && example.ideal_response.length > 200 && (
            <button onClick={() => setExpanded(false)} className="text-lex-accent hover:underline ml-1 block mt-1">show less</button>
          )}
        </p>
      </div>
    </div>
  );
};

export default FewShotReviewDashboard;
